import { useState, useEffect, useRef } from "react";
import { Calendar } from "lucide-react";

/**
 * Champ de saisie date FR avec masque jj/mm/aaaa + bouton calendrier natif.
 * - Accepte saisie clavier au format français (jj/mm/aaaa, slashes ajoutés automatiquement)
 * - L'utilisateur peut aussi cliquer sur l'icône calendrier pour ouvrir le picker natif
 * - Valeur exposée vers le parent au format ISO YYYY-MM-DD (compatible backend)
 */
export default function DateInputFR({ value, onChange, className = "", testid, placeholder = "jj/mm/aaaa", ...rest }) {
  // value (depuis le parent) est en ISO YYYY-MM-DD ; display est en jj/mm/aaaa
  const isoToFr = (iso) => {
    if (!iso || iso.length < 10) return "";
    const [y, m, d] = iso.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  };
  const frToIso = (fr) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(fr);
    if (!m) return "";
    return `${m[3]}-${m[2]}-${m[1]}`;
  };

  const [display, setDisplay] = useState(isoToFr(value));
  const hiddenDateRef = useRef(null);

  useEffect(() => { setDisplay(isoToFr(value)); }, [value]);

  const handleTextChange = (e) => {
    let raw = e.target.value.replace(/[^\d]/g, "").slice(0, 8);
    let formatted = raw;
    if (raw.length > 4) formatted = `${raw.slice(0, 2)}/${raw.slice(2, 4)}/${raw.slice(4)}`;
    else if (raw.length > 2) formatted = `${raw.slice(0, 2)}/${raw.slice(2)}`;
    setDisplay(formatted);
    const iso = frToIso(formatted);
    if (iso) onChange(iso);
    else if (formatted === "") onChange("");
  };

  const handleDatePicker = (e) => {
    const iso = e.target.value;
    if (iso) { setDisplay(isoToFr(iso)); onChange(iso); }
  };

  const openPicker = () => {
    if (!hiddenDateRef.current) return;
    try { hiddenDateRef.current.showPicker(); }
    catch { hiddenDateRef.current.focus(); hiddenDateRef.current.click(); }
  };

  return (
    <div className="relative">
      <input
        type="text"
        inputMode="numeric"
        value={display}
        onChange={handleTextChange}
        placeholder={placeholder}
        maxLength={10}
        className={`flex h-10 w-full border border-input bg-background px-3 py-2 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${className}`}
        data-testid={testid}
        {...rest}
      />
      <button
        type="button"
        onClick={openPicker}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-[#662483]"
        aria-label="Ouvrir le calendrier"
        data-testid={testid ? `${testid}-picker` : undefined}
      >
        <Calendar className="w-4 h-4" />
      </button>
      <input
        ref={hiddenDateRef}
        type="date"
        value={value || ""}
        onChange={handleDatePicker}
        className="absolute right-2 top-1/2 -translate-y-1/2 w-0 h-0 opacity-0 pointer-events-none"
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  );
}
